import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileArchive, FileText, FileUp, LoaderCircle, Save, Wand2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type DocumentField = {
  fieldKey: string;
  label: string;
  description?: string;
  exampleValue?: string;
  valueType: "string" | "date" | "number" | "boolean";
  isRequired?: boolean;
  groupName: string;
  sortOrder: number;
};

type DocumentTemplate = {
  id: string;
  templateKey: string;
  module: string;
  documentType: string;
  name: string;
  description: string | null;
  builderKey: string;
  mergeSchemaVersion: number;
  templateFileKey: string | null;
  status: "draft" | "published" | "archived";
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export function DokumentmalDetailPage() {
  const { templateKey } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [fields, setFields] = useState<DocumentField[]>([]);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"draft" | "published" | "archived">("draft");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [renderingExample, setRenderingExample] = useState(false);
  const [renderJobStatus, setRenderJobStatus] = useState<string | null>(null);
  const [renderJobError, setRenderJobError] = useState<{ code?: string | null; message: string; detail?: string | null } | null>(null);

  useEffect(() => {
    if (!templateKey) {
      return;
    }

    void bootstrap(templateKey);
  }, [templateKey]);

  const groupedFields = useMemo(() => {
    return fields.reduce<Record<string, DocumentField[]>>((groups, field) => {
      const group = groups[field.groupName] ?? [];
      group.push(field);
      groups[field.groupName] = group.sort((a, b) => a.sortOrder - b.sortOrder);
      return groups;
    }, {});
  }, [fields]);

  const hasUnsavedMetadata =
    template !== null &&
    (name !== template.name ||
      description !== (template.description ?? "") ||
      status !== template.status ||
      isActive !== template.isActive);

  async function bootstrap(key: string) {
    setLoading(true);
    setError(null);

    try {
      const templateRes = await fetch(`${API}/api/admin/document-templates/templates/by-key/${key}`, {
        credentials: "include",
      });

      if (!templateRes.ok) {
        throw new Error("Kunne ikke hente dokumentmal.");
      }

      const templateData = (await templateRes.json()) as DocumentTemplate;
      const fieldsRes = await fetch(`${API}/api/admin/document-templates/templates/${templateData.id}/fields`, {
        credentials: "include",
      });

      if (!fieldsRes.ok) {
        throw new Error("Kunne ikke hente placeholderfelt.");
      }

      const fieldsData = (await fieldsRes.json()) as DocumentField[];

      setTemplate(templateData);
      setFields(fieldsData);
      setName(templateData.name);
      setDescription(templateData.description ?? "");
      setStatus(templateData.status);
      setIsActive(templateData.isActive);

      if (templateData.templateFileKey) {
        const downloadRes = await fetch(`${API}/api/admin/document-templates/templates/${templateData.id}/download-url`, {
          credentials: "include",
        });
        if (downloadRes.ok) {
          const data = (await downloadRes.json()) as { downloadUrl: string };
          setDownloadUrl(data.downloadUrl);
        } else {
          setDownloadUrl(null);
        }
      } else {
        setDownloadUrl(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke hente dokumentmal.");
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  }

  async function saveMetadata() {
    if (!template) {
      return;
    }

    setSavingMeta(true);
    setError(null);

    try {
      const response = await fetch(`${API}/api/admin/document-templates/templates/${template.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          status,
          isActive,
        }),
      });

      if (!response.ok) {
        throw new Error("Kunne ikke lagre metadata.");
      }

      await bootstrap(template.templateKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke lagre metadata.");
    } finally {
      setSavingMeta(false);
    }
  }

  async function uploadFile(file: File) {
    if (!template) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/api/admin/document-templates/templates/${template.id}/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Kunne ikke laste opp dokumentmal.");
      }

      await bootstrap(template.templateKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke laste opp dokumentmal.");
    } finally {
      setUploading(false);
    }
  }

  async function renderExample() {
    if (!template) {
      return;
    }

    setRenderingExample(true);
    setError(null);
    setRenderJobError(null);

    try {
      const response = await fetch(`${API}/api/admin/document-templates/templates/${template.id}/render-example`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Kunne ikke generere eksempel.");
      }

      const queued = (await response.json()) as { jobId: string; status: string };
      setRenderJobStatus(queued.status);

      for (let attempt = 0; attempt < 40; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1500));

        const statusResponse = await fetch(`${API}/api/admin/document-templates/render-jobs/${queued.jobId}`, {
          credentials: "include",
        });

        if (!statusResponse.ok) {
          throw new Error("Kunne ikke hente jobbstatus.");
        }

        const statusData = (await statusResponse.json()) as {
          status: string;
          error?: string;
          errorCode?: string | null;
          errorDetail?: string | null;
          result?: { downloadUrl: string; fileName: string };
        };

        setRenderJobStatus(statusData.status);

        if (statusData.status === "completed" && statusData.result?.downloadUrl) {
          const link = document.createElement("a");
          link.href = statusData.result.downloadUrl;
          link.download = statusData.result.fileName;
          link.click();
          setRenderJobStatus(null);
          return;
        }

        if (statusData.status === "failed") {
          setRenderJobError({
            code: statusData.errorCode ?? null,
            message: statusData.error || "Dokumentjobben feilet.",
            detail: statusData.errorDetail ?? null,
          });
          throw new Error(statusData.error || "Dokumentjobben feilet.");
        }
      }

      throw new Error("Dokumentjobben brukte for lang tid.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke generere eksempel.");
    } finally {
      setRenderingExample(false);
    }
  }

  function openUploadPicker() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        void uploadFile(file);
      }
    };
    input.click();
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Dokumentmalen ble ikke funnet.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dev/dokumentmaler")} className="mt-0.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>{template.name}</h1>
            <Badge variant={template.status === "published" ? "success" : "secondary"}>{template.status}</Badge>
            <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border">{template.templateKey}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {template.module} / {template.documentType} · Buildernøkkel: {template.builderKey}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive">
          {error}
        </div>
      )}

      {renderJobStatus && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
          Genererer PDF: <span className="font-semibold uppercase text-xs tracking-wider">{renderJobStatus}</span>
        </div>
      )}

      {renderJobError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
          <div className="font-semibold flex items-center gap-2">
            Renderfeil {renderJobError.code && <span>({renderJobError.code})</span>}
          </div>
          <div className="mt-1">{renderJobError.message}</div>
          {renderJobError.detail && <div className="mt-2 text-xs opacity-80 whitespace-pre-wrap">{renderJobError.detail}</div>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="border-b border-border/50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Maldokument (.docx)</CardTitle>
                  <CardDescription>DOCX-filen som fungerer som layout.</CardDescription>
                </div>
                <FileArchive className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {!template.templateFileKey ? (
                <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-border rounded-xl bg-muted/20">
                  <p className="text-sm text-muted-foreground mb-4">Ingen fil er lastet opp for denne malen ennå.</p>
                  <Button variant="secondary" className="gap-2" onClick={() => openUploadPicker()} disabled={uploading}>
                    {uploading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                    Last opp malfil
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-muted/40 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">Gjeldende mal versjon {template.version}</p>
                        <p className="text-xs text-muted-foreground">Lastet opp {new Date(template.updatedAt).toLocaleDateString("nb-NO")}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="gap-2" onClick={() => downloadUrl && window.open(downloadUrl, "_blank", "noopener,noreferrer")} disabled={!downloadUrl}>
                        <Download className="h-3.5 w-3.5" /> Last ned
                      </Button>
                      <Button variant="secondary" size="sm" className="gap-2" onClick={() => openUploadPicker()} disabled={uploading}>
                        {uploading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
                        Erstatt
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50">
                    <div className="flex items-start gap-3">
                      <Wand2 className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-amber-900">Test generering</p>
                        <p className="text-xs text-amber-800 leading-relaxed">
                          Generer en test-PDF med eksempeldata for å verifisere at alle tags og placeholders fungerer som de skal.
                        </p>
                        <Button
                          size="sm"
                          className="mt-2 gap-2"
                          onClick={() => void renderExample()}
                          disabled={renderingExample}
                        >
                          {renderingExample ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                          Generer PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-base font-semibold">Placeholders & Felt</CardTitle>
              <CardDescription>Tags du kan bruke i DOCX-filen.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-8">
                {Object.entries(groupedFields).map(([groupName, groupFields]) => (
                  <div key={groupName} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{groupName}</h3>
                      <Badge variant="muted" className="text-[9px] px-1.5 h-4">{groupFields.length} felt</Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {groupFields.map((field) => (
                        <div key={field.fieldKey} className="p-3 border border-border rounded-xl bg-muted/10 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <code className="px-1.5 py-0.5 rounded bg-foreground text-background text-[11px] font-mono leading-none">{"{"}{field.fieldKey}{"}"}</code>
                            <span className="text-[9px] font-semibold text-muted-foreground uppercase">{field.valueType}</span>
                          </div>
                          <div className="text-xs font-semibold text-foreground">{field.label}</div>
                          {field.description && <p className="text-[11px] text-muted-foreground leading-relaxed">{field.description}</p>}
                          {field.exampleValue && (
                            <div className="text-[10px] text-muted-foreground/80 italic border-t border-border/50 pt-1.5 mt-1.5">
                              Eks: {field.exampleValue}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-sm font-semibold">Administrasjon</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Navn i systemet</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Beskrivelse</Label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full min-h-[80px] rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    className="w-full h-10 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Aktiv</Label>
                  <div className="flex h-10 items-center gap-2 px-3 border border-border rounded-lg bg-muted/20">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} id="is-active" />
                    <Label htmlFor="is-active" className="text-xs cursor-pointer">I bruk</Label>
                  </div>
                </div>
              </div>
              <Button
                className="w-full gap-2"
                onClick={() => void saveMetadata()}
                disabled={!hasUnsavedMetadata || savingMeta || !name.trim()}
              >
                {savingMeta ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Lagre metadata
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-sm font-semibold">Info</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Buildervariant</span>
                <Badge variant="muted">{template.builderKey}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Modul</span>
                <span className="font-medium">{template.module}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Opprettet</span>
                <span className="text-xs">{new Date(template.createdAt).toLocaleDateString("nb-NO")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sist oppdatert</span>
                <span className="text-xs">{new Date(template.updatedAt).toLocaleDateString("nb-NO")}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
